<template>
  <LcHighFrequencyTeaserWatch
    v-bind="{ ballConfig, isOperational, gameNumber, to }"
    :style="theme"
    :game-label="$t('highfrequencygame.teaserwatch.game.label')"
    :timer-value-label="$t('highfrequencygame.teaserwatch.timervaluelabel')"
    :timer-unit-label="$t('highfrequencygame.teaserwatch.timerunitlabel')"
    :title="$t('highfrequencygame.teaserwatch.title').toUpperCase()"
  >
    <template #message>
      <span v-if="isOperational" class="high-frequency-game-message">
        {{ $t('highfrequencygame.teaserwatch.message.operating') }}
      </span>
      <i18n-t
        scope="global"
        tag="span"
        class="high-frequency-game-message text-center"
        :keypath="pausedKeypath"
        :plural="resumeInDays || undefined"
        v-else-if="operatingHours"
      >
        <template #time>
          <NuxtTime :datetime="operatingHours.nextStart" :hour12="true" time-style="short" />
        </template>
      </i18n-t>
    </template>
    <template #new-game-label>
      <span class="high-frequency-game-label">
        {{ $t('highfrequencygame.teaserwatch.newgame.label') }}
      </span>
    </template>
  </LcHighFrequencyTeaserWatch>
</template>

<script lang="ts" setup>
import type {
  LcHighFrequencyTeaserWatchProps,
  LcHighFrequencyBallConfig,
} from '@lottocom/frontend-components'

import { HighFrequencyGameId } from '@lottocom/high-frequency-game-mapping'

import { OperatingHoursRange } from '../../../../../core/api/shared/@types/generated/backend/graphql-schema-types'

type Props = Pick<LcHighFrequencyTeaserWatchProps, 'gameNumber' | 'isOperational'> & {
  id: HighFrequencyGameId
  licensedTerritory: string
  operatingHours?: OperatingHoursRange
}

const BASE_BALL_CONFIG: LcHighFrequencyBallConfig[] = [
  { left: '87%', opacity: 0.4, size: 'small', top: '44%' },
  { left: '60%', opacity: 0.4, size: 'small', top: '6%' },
  { left: '12%', opacity: 0.4, size: 'small', top: '40%' },
  { left: '28%', opacity: 0.4, size: 'small', top: '92%' },
  { left: '78%', opacity: 0.4, size: 'small', top: '88%' },
]

const { gameNumber, id, isOperational, licensedTerritory, operatingHours } = defineProps<Props>()

const localePath = useLocalePath()

const resumeInDays = useHighFrequencyTeaserResumeDays(() => (isOperational ? null : operatingHours))

const pausedKeypath = computed(() =>
  resumeInDays.value
    ? 'highfrequencygame.teaserwatch.message.paused.laterday'
    : 'highfrequencygame.teaserwatch.message.paused',
)

const ballConfig = useState(`${id}-HighFrequencyTeaserWatchBallConfig`, () =>
  BASE_BALL_CONFIG.map((option) => ({
    ...option,
    content: Math.floor(Math.random() * 50 + 10).toString(),
  })),
)

const theme = computed(() => {
  const { createCssVar } = getLotteryCssVarConstructor(licensedTerritory, id)

  return {
    '--high-frequency-ball-background': createCssVar('high-frequency-ball-background'),
    '--high-frequency-ball-color': createCssVar('high-frequency-ball-text-color'),
    '--high-frequency-game-background-color': createCssVar(
      'high-frequency-teaser-background-color',
    ),
    '--high-frequency-game-background-gradient': createCssVar(
      'high-frequency-teaser-background-gradient',
    ),
    '--high-frequency-teaser-watch-arrows-color': createCssVar(
      'high-frequency-teaser-watch-icon-arrows-color',
    ),
    '--high-frequency-teaser-watch-arrows-color-fade': createCssVar(
      'high-frequency-teaser-watch-icon-arrows-color-fade',
    ),
    '--high-frequency-teaser-watch-background': createCssVar(
      'high-frequency-teaser-watch-background-gradient',
    ),
    '--high-frequency-teaser-watch-footer-color': createCssVar(
      'high-frequency-teaser-watch-footer-color',
    ),
    '--high-frequency-teaser-watch-game-background-gradient': createCssVar(
      'high-frequency-teaser-watch-game-background-gradient',
    ),
    '--high-frequency-teaser-watch-game-badge-background-color': createCssVar(
      'high-frequency-teaser-watch-badge-background',
    ),
    '--high-frequency-teaser-watch-game-badge-border-color': createCssVar(
      'high-frequency-teaser-watch-badge-border-color',
    ),
    '--high-frequency-teaser-watch-game-number-color': createCssVar(
      'high-frequency-teaser-watch-badge-game-color',
    ),
    '--high-frequency-teaser-watch-game-number-label-color': createCssVar(
      'high-frequency-teaser-watch-badge-text-color',
    ),
    '--high-frequency-teaser-watch-new-game-label-color': createCssVar(
      'high-frequency-teaser-watch-new-game-color',
    ),
    '--high-frequency-teaser-watch-pause-icon-color': createCssVar(
      'high-frequency-teaser-watch-icon-pause-color',
    ),
    '--high-frequency-teaser-watch-play-button-drop-shadow-color': createCssVar(
      'high-frequency-teaser-watch-play-drop-shadow-color',
    ),
    '--high-frequency-teaser-watch-play-button-icon-color-end': createCssVar(
      'high-frequency-teaser-watch-play-color-end',
    ),
    '--high-frequency-teaser-watch-play-button-icon-color-start': createCssVar(
      'high-frequency-teaser-watch-play-color-start',
    ),
    '--high-frequency-teaser-watch-play-button-shadow-color': createCssVar(
      'high-frequency-teaser-watch-play-shadow-color',
    ),
    '--high-frequency-teaser-watch-timer-color': createCssVar(
      'high-frequency-teaser-watch-icon-timer-color',
    ),
    '--high-frequency-teaser-watch-title-color': createCssVar(
      'high-frequency-teaser-watch-title-color',
    ),
    '--lottery-jackpot-color': createCssVar('high-frequency-teaser-jackpot-color'),
    '--lottery-jackpot-text-color': createCssVar('high-frequency-teaser-text-color'),
    '--teaser-frame-color': createCssVar('color-accent'),
  }
})

const to = computed(() => localePath('/keno/watch'))
</script>

<style lang="scss" scoped>
.high-frequency-game-message,
.high-frequency-game-label {
  white-space: pre-line;
}
</style>
